'use client'

import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useParams } from 'next/navigation'
import { responseErrorMessage } from '@/lib/api/error-message'
import { ApiError } from '@/lib/api/request'
import type { PropertyDisplayNameMode } from '@/lib/properties/display-name'
import type { VendorPropertyDetailVisibility } from '@/lib/vendors/property-detail-visibility'
import type { WorkforceShiftMode } from '@/lib/workforce/member'

// Workspace type enum (matches Prisma WorkspaceType)
export type WorkspaceType = 'PROPERTY_MANAGER' | 'VENDOR'

// Extended workspace type with member info
export interface DbWorkspaceWithMember {
  country: string
  created_at: string
  currency: string
  id: string
  language: string
  /** Public URL from `workspaces.logo_url` (Supabase snake_case). */
  logo_url?: string | null
  member_permissions?: Record<string, unknown>
  // Member-specific fields (from join)
  member_role?: string
  name: string
  organization_id: string | null
  settings: Record<string, unknown>
  slug: string | null
  timezone: string
  type?: WorkspaceType
  updated_at: string
}

export interface UserWorkspacesPage {
  items: DbWorkspaceWithMember[]
  nextOffset: number | null
  pinnedItems: DbWorkspaceWithMember[]
}

// =============================================================================
// QUERY OPTIONS
// =============================================================================

export function shouldRetryWorkspaceBootstrapQuery(failureCount: number, error: Error): boolean {
  if (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 429
  ) {
    return false
  }

  return failureCount < 1
}

export async function workspaceBootstrapFetchError(
  response: Response,
  fallback: string
): Promise<ApiError> {
  return new ApiError(await responseErrorMessage(response, fallback), response.status)
}

export function workspaceQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      const response = await fetch(`/api/workspaces/${workspaceId}`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        throw await workspaceBootstrapFetchError(response, 'Failed to load workspace')
      }
      return response.json() as Promise<DbWorkspaceWithMember>
    },
    retry: shouldRetryWorkspaceBootstrapQuery,
    staleTime: 1000 * 60 * 10, // 10 minutes - workspace data rarely changes
  })
}

function userWorkspacesQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ['workspaces', userId],
    queryFn: async () => {
      const response = await fetch('/api/workspaces', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        throw await workspaceBootstrapFetchError(response, 'Failed to load workspaces')
      }
      return response.json() as Promise<DbWorkspaceWithMember[]>
    },
    retry: shouldRetryWorkspaceBootstrapQuery,
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Get the current workspace ID from URL params.
 * Use this when you just need the ID without fetching workspace data.
 */
export function useWorkspaceId(): string | null {
  const params = useParams()
  const { workspaceId } = params

  if (!workspaceId) {
    return null
  }
  return Array.isArray(workspaceId) ? workspaceId[0] : workspaceId
}

/**
 * Fetch the current workspace data from URL params.
 * Returns workspace details with the current user's role and permissions.
 */
export function useWorkspace(options?: { enabled?: boolean }) {
  const workspaceId = useWorkspaceId()

  return useQuery({
    ...workspaceQueryOptions(workspaceId ?? ''),
    enabled: !!workspaceId && (options?.enabled ?? true),
  })
}

/**
 * Fetch all workspaces for a user.
 */
export function useUserWorkspaces(userId: string) {
  return useQuery({
    ...userWorkspacesQueryOptions(userId),
    enabled: !!userId,
  })
}

export function useInfiniteUserWorkspaces(
  userId: string,
  search: string,
  options?: { enabled?: boolean; pinnedIds?: string[] }
) {
  const pinnedIds = options?.pinnedIds ?? []
  return useInfiniteQuery<UserWorkspacesPage>({
    queryKey: ['workspaces', userId, 'infinite', search, pinnedIds],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        paginated: 'true',
        offset: String(pageParam),
      })
      if (search) {
        params.set('q', search)
      }
      for (const pinnedId of pinnedIds) {
        params.append('pinned', pinnedId)
      }

      const response = await fetch(`/api/workspaces?${params.toString()}`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        throw await workspaceBootstrapFetchError(response, 'Failed to load workspaces')
      }
      return response.json() as Promise<UserWorkspacesPage>
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    enabled: !!userId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 10,
  })
}

interface VendorContext {
  hasMultiplePmWorkspaces?: boolean
  hideReservationGuestDetails?: boolean
  links?: VendorContextLink[]
  pmWorkspaceId?: string
  pmWorkspaceIds?: string[]
  propertyDisplayNameMode?: PropertyDisplayNameMode
  propertyDisplayNameModesByWorkspaceId?: Record<string, PropertyDisplayNameMode>
  propertyIds: string[]
  taskTypes: string[]
  /** When workspace is VENDOR, the organization ID to filter tasks (assigned_to_vendor_id). */
  vendorOrgId?: string
}

export interface VendorContextLink {
  visibleSectionsByProperty?: Record<string, string[]>
  allowVendorAdminTaskDelete: boolean
  allowVendorAdminTaskReschedule: boolean
  hideReservationGuestDetails: boolean
  pmWorkspaceId: string
  pmWorkspaceName: string
  propertyDetailVisibility: VendorPropertyDetailVisibility
  propertyDisplayNameMode: PropertyDisplayNameMode
  propertyIds: string[]
  taskTypes: string[]
  vendorLinkId: string
}

/**
 * Fetch vendor context (propertyIds, taskTypes) for the current workspace when it is type VENDOR.
 * Use for calendar, dashboard, schedule to filter data.
 */
export function useVendorContext(workspaceId: string | null) {
  return useQuery({
    queryKey: ['vendor-context', workspaceId],
    queryFn: async (): Promise<VendorContext> => {
      if (!workspaceId) {
        return { propertyIds: [], taskTypes: [] }
      }
      const response = await fetch(`/api/workspaces/${workspaceId}/vendor-context`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        const message =
          response.status === 403
            ? 'You do not have access to this vendor workspace.'
            : 'Vendor workspace context could not be loaded.'
        throw new Error(message)
      }

      const data: VendorContext = await response.json()
      return data
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Get workspace settings with proper typing.
 */
export function useWorkspaceSettings<T = Record<string, unknown>>(
  workspaceId: string,
  options?: { enabled?: boolean }
) {
  const { data: workspace, ...rest } = useQuery({
    ...workspaceQueryOptions(workspaceId),
    enabled: !!workspaceId && (options?.enabled ?? true),
  })

  return {
    ...rest,
    data: workspace?.settings as T | undefined,
    workspace,
  }
}

// =============================================================================
// WORKSPACE MEMBERS
// =============================================================================

export interface WorkspaceMember {
  /** Month-day (MM-DD) of work anniversary, calendar projection only. */
  anniversaryMonthDay?: string | null
  /** Month-day (MM-DD) of birthday, calendar projection only — year is never
   *  exposed here (full DOB is gated to workforce.approve, PROD-3935). */
  birthdayMonthDay?: string | null
  canReceiveCalls?: boolean
  contactAvatarUrl?: string | null
  contactId: string | null
  createdAt: string
  customRole: {
    id: string
    name: string
    slug: string
    color: string
    icon: string
    roleType: string
    permissions: Record<string, string[]>
  } | null
  customRoleId: string | null
  departmentMemberships?: Array<{
    departmentId: string
    /** Whether this member is a lead of the department (PROD-3935, Option B). */
    isLead?: boolean
    createdAt: string
    department: {
      id: string
      name: string
      slug: string
      color: string | null
      icon: string | null
      isFieldOps: boolean
      isDefault: boolean
      defaultShowInSchedule?: boolean | null
      defaultShiftMode?: WorkforceShiftMode | null
    } | null
  }>
  externalWorkerId?: string | null
  /** Hire year (for years-of-service), calendar projection only — the exact
   *  full hire date is gated to personalInfo:view via the profile route. */
  hireYear?: number | null
  id: string
  isActive?: boolean
  permissions: Record<string, unknown> | null
  role: string
  shiftMode?: WorkforceShiftMode
  showInSchedule: boolean
  user: {
    email: string
    user_metadata: {
      first_name?: string | null
      last_name?: string | null
      full_name?: string | null
      name?: string | null
      avatar_url?: string | null
    }
  } | null
  userId: string
  workspaceId: string
}

type WorkspaceMembersProjection = 'full' | 'calendar' | 'schedule'

interface WorkspaceMembersRequestOptions {
  includeDepartments?: boolean
  projection?: WorkspaceMembersProjection
}

interface UseWorkspaceMembersOptions extends WorkspaceMembersRequestOptions {
  enabled?: boolean
}

export function workspaceMembersQueryOptions(
  workspaceId: string,
  options: WorkspaceMembersRequestOptions = {}
) {
  const projection = options.projection ?? 'full'
  const includeDepartments = options.includeDepartments ?? false

  return queryOptions({
    queryKey: ['workspace-members', workspaceId, projection, includeDepartments],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (projection !== 'full') {
        params.set('projection', projection)
      }
      if (includeDepartments) {
        params.set('includeDepartments', 'true')
      }

      const query = params.toString()
      const response = await fetch(
        `/api/workspaces/${workspaceId}/members${query ? `?${query}` : ''}`
      )
      if (!response.ok) {
        throw new Error('failedToFetchWorkspaceMembers')
      }
      return response.json() as Promise<WorkspaceMember[]>
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Fetch all members of a workspace.
 */
export function useWorkspaceMembers(workspaceId: string, options?: UseWorkspaceMembersOptions) {
  return useQuery({
    ...workspaceMembersQueryOptions(workspaceId, {
      projection: options?.projection,
      includeDepartments: options?.includeDepartments,
    }),
    enabled: !!workspaceId && (options?.enabled ?? true),
  })
}
